// Notification Service — client-side with localStorage persistence

export type NotificationType = 'global' | 'faction' | 'individual';

export interface GameNotification {
    id: string;
    type: NotificationType;
    messageKey: string;         // translation key, e.g. 'notif.zoneCaptured'
    params?: Record<string, string>; // interpolation params for t()
    timestamp: number;
    read: boolean;
}

const STORAGE_PREFIX = 'oko_notifications_';
const MAX_NOTIFICATIONS = 100;

function storageKey(username: string): string {
    return `${STORAGE_PREFIX}${username}`;
}

export function getNotifications(username: string): GameNotification[] {
    try {
        const raw = localStorage.getItem(storageKey(username));
        if (!raw) return [];
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed : [];
    } catch {
        return [];
    }
}

function saveNotifications(username: string, notifications: GameNotification[]): void {
    try {
        // Keep only the latest MAX_NOTIFICATIONS
        const trimmed = notifications.slice(-MAX_NOTIFICATIONS);
        localStorage.setItem(storageKey(username), JSON.stringify(trimmed));
    } catch { }
}

export function addNotification(
    username: string,
    type: NotificationType,
    messageKey: string,
    params?: Record<string, string>
): GameNotification {
    const notif: GameNotification = {
        id: `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
        type,
        messageKey,
        params,
        timestamp: Date.now(),
        read: false,
    };
    const list = getNotifications(username);
    list.push(notif);
    saveNotifications(username, list);
    return notif;
}

export function markRead(username: string, id: string): void {
    const list = getNotifications(username);
    const idx = list.findIndex(n => n.id === id);
    if (idx >= 0) {
        list[idx] = { ...list[idx], read: true };
        saveNotifications(username, list);
    }
}

export function markAllRead(username: string): void {
    const list = getNotifications(username);
    const updated = list.map(n => n.read ? n : { ...n, read: true });
    saveNotifications(username, updated);
}

export function getUnreadCount(username: string): number {
    return getNotifications(username).filter(n => !n.read).length;
}

export function clearAll(username: string): void {
    try {
        localStorage.removeItem(storageKey(username));
    } catch { }
}
